import { Component, Vue, Prop as prop, Watch as watch } from 'nuxt-property-decorator';
import {mapGetters, mapState} from 'vuex';
import { AxiosError } from 'axios';
import { Context } from '@nuxt/types';
import { trackProductView } from '~/lib/metrika';
import type { Product, ProductRating, TableTop } from '~/types/product';
import { OfferList, OfferRefInfo, OfferRefOptionContainer } from '~/types/catalog';
import { IMainLocation } from '~/types/region';

@Component({
    components: {
        btn: require('~/components/form/button/').default,
        slider: require('~/components/common/gallery/').default,
        offer: require('~/components/catalog/offer/').default,
        loading: require('~/components/common/loader/').default,
        rating: require('~/components/common/rating/').default,
        catalog: require('~/components/catalog/offer-horisontal-slider/').default,
        reviews: require('~/components/pages/product/reviews').default,
        favoriteBtn: require('~/components/pages/product/favorite-btn').default,
        recentViewList: require('~/components/catalog/recent-view-list').default,
        cheapRequest: require('~/components/pages/product/cheap-request').default,
        refSelector: require('~/components/pages/product/offer-ref-selector').default,
        descriptionContainer: require('~/components/pages/product/description-block').default,
        simplePicture: require('~/components/common/image/').default,
        storesAvailability: require('~/components/pages/product/stores-availability').default,
        creditInfo: require('~/components/pages/product/credit').default,
        documents: require('~/components/pages/product/documents').default,
        configurator: require('~/components/pages/product/configurator').default,
        yandexPayWidget: require('~/components/catalog/yandex-pay-widget').default,
        couponComponent: require('~/components/pages/product/coupon').default,
        /** @ts-ignore */
        adminPanel: () => import('~/components/pages/product/admin-panel'),
    },
    computed: {
        ...mapState(['region', 'user']),
        ...mapGetters(['hasSplit'])
    },
    watchQuery: ['model'],
    validate: ({ params, query }) => typeof query.model === 'string' && /^[A-z-]*/.test(params.path) && /^\d/.test(query.model),
    fetchOnServer: false,
})
export default class ProductPageComponent extends Vue {

    @prop()
    public catalogId;

    @prop()
    public catalogCode;

    @prop()
    public productId;

    public user!: User;
    public region!: IMainLocation;
    public hasSplit: boolean;

    public product: Product = null;

    public tabletops: TableTop[] = null;

    public buying = false;
    private loading = false;
    public descriptions = null;
    public offerId = null;
    public saleCount = null;

    public ref = null;
    public refs: Map<number, OfferRefInfo> = new Map();
    public selectedOfferId: number = 0;
    public options: OfferRefOptionContainer = null;
    public rating: ProductRating = null;

    showDescriptions = false;

    reviewMode = 0;

    storeList = [];

    files = [];

    public lists = [];

    private params: {
        id: number,
        code: string,
        catalog: string,
    } = null;

    public openReadySets() {
        this.scrollToList(0);
    }

    scrollToList(idx) {

        this.showDescriptions = false;

        this.$nextTick(() => {
            if (this.$refs.list_ready_sets) {
                this.$nextTick(() => (this.$refs.list_ready_sets[idx] as any).scrollIntoView({
                    block: 'start',
                    behavior: 'smooth',
                }));
            }
        });
    }

    
    scrollToGroup(id: string) {

        this.$nextTick(() => {
            const element = document.getElementById(id);

            if (!element) {
                return;
            }

            let top = element.getBoundingClientRect()?.top + window.scrollY;

            if (!top) {
                element.scrollIntoView({block: "start", behavior: "smooth"});
                return;
            }

            top -= 110;

            window.scrollTo({top, behavior: "smooth"})
        });
    }


    scrollToId(id) {

        this.showDescriptions = id === 'description-container';

        this.$nextTick(() => {
            const element = document.getElementById(id);
            element?.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
            });
        });
    }

    created() {
        this.selectedOfferId = this.params.id;
    }

    public mounted() {
        this.track()
    }

    private track() {
        trackProductView({
            id: this.selectedOffer.id,
            name: this.selectedOffer.name,
            price: this.selectedOffer.price.price,
        })
    }


    public async buy() {

        this.buying = true;

        try {
            await this.$store.dispatch('cart/Add', {
                offerId: this.params.id,
            });
        } finally {
            this.buying = false;
        }
    }

    public fetch() {
        this.fetchLists();
    }

    @watch('region')
    public async updateRegion() {


        try {
            const result = await this.$store.dispatch('data/FetchOffer', this.params);

            if (result.newLink) {
                this.$router.push(result.newLink);
                return;
            }

            this.product = result.product;
            this.storeList = result.stores;
            this.files = result.files;
            this.rating = result.rating;

            this.refs = new Map();
            if (result.refs) {
                result.refs.refs.forEach((el) => {
                    this.refs.set(el.id, el);
                });
                this.ref = result.refs;
            }

            this.fetchLists();

            if (result.breadcrumbs) {
                this.$store.commit('setBreadcrumbs', result.breadcrumbs);
            }

            this.descriptions = result.meta;

            this.$nextTick(() => {
                if (this.$refs['list_ready_sets'] && Array.isArray(this.$refs['list_ready_sets'])) {
                    for (const el of (this.$refs['list_ready_sets'] as any[])) {
                        if (el.reset) {
                            el.reset();
                        }
                    }
                }
            });

        } catch (err) {
            console.error(err);
        }
    }

    @watch('$route')
    onRouteChange() {
        this.fetchLists();
    }

    private head() {
        const offerId = parseInt((this.$route.query.model as string), 10);

        const meta = [{
            vmid: 'description',
            name: 'description',
            content: this.descriptions.description,
        }];

        if (!this.product?.available) {
            meta.push({
                vmid: 'robots',
                name: 'robots',
                content: 'noindex'
            })
        }

        return {
            title: this.descriptions.title,
            meta,
            link: [{
                rel: 'canonical',
                href: `https://www.triya.ru${this.$route.path}?model=${offerId}`,
            }],
        };
    }

    private async asyncData(ctx: Context) {

        const offerId = parseInt((ctx.route.query.model as string), 10);

        if (offerId.toString() !== ctx.route.query.model) {
            ctx.redirect(301, `/catalog/${ctx.params.path}/${ctx.params.code}/?model=${offerId}`);
            return;
        }

        let offer;

        const params = {
            id: offerId,
            code: ctx.params.code,
            catalog: ctx.params.path,
        };

        const returnData: any = {
            params,
        };

        try {
            const result = await ctx.store.dispatch('data/FetchOffer', params);

            if (result.newLink) {
                ctx.redirect(result.newLink);
                return;
            }

            offer = result.product;

            returnData.storeList = result.stores;
            returnData.files = result.files;
            returnData.rating = result.rating;
            returnData.saleCount = result.saleCount;

            if (result.refs) {
                const refs = new Map();

                result.refs.refs.forEach((el) => {
                    refs.set(el.id, el);
                });

                returnData.refs = refs;
                returnData.ref = result.refs;
            }

            if (offer.newOfferLink) {
                ctx.redirect(offer.newOfferLink);
            }

            if (offer.lists && offer.lists.length) {

                for (const list of offer.lists) {
                    list.page = 1;
                }
            }

            if (result.breadcrumbs) {
                ctx.store.commit('setBreadcrumbs', result.breadcrumbs);
            }

            returnData.product = offer;
            returnData.descriptions = result.meta;

        } catch (err: any) {
            // console.error(err);

            if (typeof err.response !== 'undefined') {

                switch ((err as AxiosError).response.status) {
                    case 404:
                        ctx.error({ statusCode: 404 });
                        break;
                    default:
                        ctx.error({ statusCode: 500 });
                }
            } else {
                ctx.error({ statusCode: 500 });
            }
        }

        return returnData;
    }
    
    private async fetchLists() {
        const offerId = parseInt((this.$route.query.model as string), 10);

        this.lists = await this.$store.dispatch('data/FetchProductList', {
            id: offerId
        });

        if (this.lists?.length) {
            for (const list of this.lists) {
                list.page = 1;
            }
        }
    }

    
    get badges() {
        return new Set(this.product.badges ?? []);
    }


    private async fetchProductList(callback, list: OfferList) {

        if (list.page === list.pageCount) {
            callback.complete();
            return;
        }

        if (typeof list.page === 'undefined') {
            list.page = 1;
        }

        try {
            const result = await this.$store.dispatch('data/FetchProductListPage', {
                id: this.params.id,
                code: list.id,
                page: list.page + 1,
            });

            list.offers = list.offers.concat(result);

            list.page++;

            if (list.page === list.pageCount) {
                callback.complete();
            } else {
                callback.loaded();
            }

        } catch {
            callback.complete();
        }
    }

    updateSelectedOffer(value) {
        this.selectedOfferId = value;
        const targetOffer = this.refs.get(value);
        this.$store.state.shouldKeepScroll = true;
        this.$router.replace(targetOffer.link);
        this.track();
    }

    get discountPercent() {
        if (!this.selectedOffer?.price?.discount?.oldPrice) {
            return null;
        }
        const price = this.selectedOffer.price;

        return Math.round((price.discount.oldPrice - price.price) / price.discount.oldPrice * 100);
    }

    get selectedOffer() {

        if (this.refs) {
            return this.refs.get(this.selectedOfferId || this.params.id) || this.defaultOffer; 
        }

        return this.defaultOffer;
    }

    get defaultOffer() {
        return {
            id: this.params.id,
            name: this.descriptions.h1,
            pictures: this.product.photos,
            price: this.product.price,
        }
    }

    get lowerOfferLists() {

        return this.lists;
    }

    get hasCredit() {
        return this.region.payTypes.find((el) => el.id === 14);
    }

    get totalReviewsLabel() {
        if (!this.rating?.review_count) {
            return 'Отзывы'
        }

        return `Отзывы (${this.rating.review_count})`;
    }

    get totalAnswersLabel() {
        if (!this.rating?.questions) {
            return 'Вопросы'
        }

        return `Вопросы (${this.rating.questions})`;
    }

    get totalSell() {
        if (this.saleCount){
            return `Купили более ${this.saleCount} раз`;
        }
    }
}
